// DraftsPage — focused view on draft articles. Differs from
// ArticlesPage (status=draft filter) by lighter chrome and an emphasis on
// "what to finish next". Sort: most recently updated first.

import { useEffect, useState } from "react";
import { list, fileUrl } from "../lib/pb";
import { navigate } from "../App";
import { useT } from "../lib/i18n";
import type { Article } from "../lib/types";
import { formatDate } from "./ArticlesPage";

export default function DraftsPage() {
  const t = useT();
  const [items, setItems] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    list<Article>("articles", {
      perPage: 100,
      sort: "-updated",
      filter: "status='draft'",
      expand: "author",
    })
      .then((r) => setItems(r.items))
      .catch((e) => alert(`Failed to load drafts: ${(e as Error).message}`))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t.draftsTitle}</h1>
        <p className="page-subtitle">{t.draftsSubtitle(items.length)}</p>
      </div>

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : items.length === 0 ? (
        <div className="empty">{t.draftsEmpty}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {items.map((a) => (
            <div
              key={a.id}
              className="card card-hover"
              style={{ display: "flex", gap: 12, padding: 12, cursor: "pointer", alignItems: "stretch" }}
              onClick={() => navigate(`/article/${a.id}`)}
            >
              <div style={{ width: 96, flexShrink: 0, background: "var(--bg-3)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                {a.cover ? (
                  <img
                    src={fileUrl("articles", a.id, a.cover, "200x200")}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-4)", fontSize: 22 }}>
                    ✎
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 6 }}>
                <div>
                  <div style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontWeight: 600, color: "var(--text-0)", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {a.title || "(untitled)"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>
                    {a.summary || "No summary yet."}
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "var(--text-3)" }}>
                  <span>{a.expand?.author?.name || "—"}</span>
                  <span>{formatDate(a.updated)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
